import React, { memo, useRef, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, CircularProgress, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  Tooltip,
} from '@mui/material';
import { DeleteSweep as ClearIcon } from '@mui/icons-material';

import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatEmptyState from './ChatEmptyState';
import TapTooltip from '../../../shared/components/TapTooltip';
import { useViewport } from '../../../shared/hooks/useViewport';
import { useElapsedTime } from '../hooks/useElapsedTime';
import { useConversationMessages } from '../hooks/useConversationMessages';
import { contextPercent, formatTokens, latestContextUsage } from '../utils/tokenUsage';
import { ChatSession } from '../../../types';
import { TOUCH_TARGET_SX } from '../../../shared/constants/layout';

const STREAMING_TIMESTAMP = new Date(0).toISOString();

const StreamingMessage = memo<{ content: string; investigationId: string }>(({ content, investigationId }) => {
  const message = useMemo(() => ({
    id: 'streaming',
    content,
    role: 'assistant' as const,
    timestamp: STREAMING_TIMESTAMP,
    investigation_id: investigationId,
  }), [content, investigationId]);

  return <ChatMessage key="streaming-message" message={message} />;
});
StreamingMessage.displayName = 'StreamingMessage';

interface ChatInterfaceProps {
  currentSession: ChatSession | null;
  customerId: string;
  currentStreamingMessage: string;
  isAgentTyping: boolean;
  agentActivity: string | null;
  isLoading: boolean;
  operationStartTime: number | null;
  contextUsage: { contextTokens: number; maxContextTokens: number | null } | null;
  messageQueue: string[];
  sendMessage: (message: string) => Promise<void>;
  cancelStream: () => Promise<void>;
  clearConversation: () => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  onAddAsFinding: (content: string) => void;
  // The procedure row shown above the box. Passed in rather than built here so the chat does not
  // need to know what a playbook or a workflow is, or how either one starts.
  procedure?: React.ReactNode;
  // The empty state's way forward when no investigation is selected.
  onNewInvestigation: () => void;
  onOpenInvestigations: () => void;
}

export const ChatInterface = memo<ChatInterfaceProps>(({
  currentSession,
  customerId,
  currentStreamingMessage,
  isAgentTyping,
  agentActivity,
  isLoading,
  operationStartTime,
  contextUsage,
  messageQueue,
  sendMessage,
  cancelStream,
  clearConversation,
  editMessage,
  onAddAsFinding,
  procedure,
  onNewInvestigation,
  onOpenInvestigations,
}) => {
  // Conversation message list comes off the query cache (ADR-0049), not the
  // session; streaming appends land here via setQueryData.
  const { data: messages = [] } = useConversationMessages(customerId, currentSession?.id);

  // Calculate elapsed time for progress indication (only show after 3 seconds)
  const elapsedTime = useElapsedTime(operationStartTime, isLoading || isAgentTyping);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isInitialScrollRef = useRef<string | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Use instant scroll for initial conversation load, smooth for updates
    const isInitialLoad = currentSession?.id !== isInitialScrollRef.current;

    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: isInitialLoad ? 'auto' : 'smooth'
      });
    });

    // Track that we've scrolled for this session
    if (isInitialLoad && currentSession?.id) {
      isInitialScrollRef.current = currentSession.id;
    }
  }, [messages, currentStreamingMessage, currentSession?.id]);

  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const { isPhone, isTouch } = useViewport();
  const hasMessages = messages.length > 0;

  // Live state wins; loaded history fills the gap after a reload, when the reducer
  // has seen no turn complete yet and would otherwise show nothing.
  const effectiveContextUsage = useMemo(
    () => contextUsage ?? latestContextUsage(messages),
    [contextUsage, messages],
  );

  const contextPct = contextPercent(
    effectiveContextUsage?.contextTokens,
    effectiveContextUsage?.maxContextTokens,
  );

  const isStreaming = isAgentTyping || isLoading;
  // The chip still appears with a known context size but no declared window —
  // it shows the token count alone rather than a percentage of nothing.
  const showContextChip = effectiveContextUsage !== null || isStreaming;

  const isDisabled = !currentSession || currentSession?.state === 'archived' || currentSession?.state === 'completed';

  return (
    <>
      {/* Token usage + clear conversation header */}
      {currentSession && hasMessages && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          px: 2,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 36,
        }}>
          {showContextChip && (
            <TapTooltip
              title={
                effectiveContextUsage
                  ? effectiveContextUsage.maxContextTokens
                    ? `Context window: ${effectiveContextUsage.contextTokens.toLocaleString()} / ${effectiveContextUsage.maxContextTokens.toLocaleString()} tokens (prompt incl. cached)`
                    : `Context in use: ${effectiveContextUsage.contextTokens.toLocaleString()} tokens — this agent's spec declares no context window, so there is no percentage to show`
                  : 'Calculating context usage...'
              }
            >
              <Chip
                size="small"
                label={
                  isStreaming && effectiveContextUsage === null ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={10} sx={{ color: 'inherit' }} />
                      <span>context</span>
                    </Box>
                  ) : isStreaming ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CircularProgress size={10} sx={{ color: 'inherit' }} />
                      <span>{contextPct !== null ? `${contextPct}%` : formatTokens(effectiveContextUsage!.contextTokens)}</span>
                    </Box>
                  ) : contextPct !== null ? (
                    `${contextPct}% context`
                  ) : (
                    `${formatTokens(effectiveContextUsage!.contextTokens)} context`
                  )
                }
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', height: 24 }}
              />
            </TapTooltip>
          )}
          <Tooltip title="Clear the conversation: the agent forgets every message in this investigation and starts fresh. Findings and knowledge items are kept.">
            <span>
              <IconButton
                size="small"
                onClick={() => setConfirmClearOpen(true)}
                disabled={isLoading || isAgentTyping}
                aria-label="Clear conversation"
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 },
                  ...(isTouch && TOUCH_TARGET_SX),
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear conversation?</DialogTitle>
        <DialogContent>
          {/* The tap that opens this dialog is the touch user's only way to read the
              clear button's hover hint, so on touch the dialog carries it in full. */}
          <DialogContentText>
            {isTouch
              ? 'The agent forgets every message in this investigation and starts fresh. Findings and knowledge items will be kept.'
              : 'Findings and knowledge items will be kept.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmClearOpen(false);
              clearConversation();
            }}
            color="error"
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Messages */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
        }}
      >
        {currentSession ? (
          <Container maxWidth={false} sx={{ py: 2, px: isPhone ? 0.5 : 3 }}>
            {messages.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  py: 8,
                }}
              >
                <Box>
                  <Typography variant="h4" gutterBottom color="primary">
                    Investigation Assistant
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your AI-powered investigation platform for security analysis and knowledge discovery.
                    Conduct thorough investigations, analyze findings, and discover relevant knowledge.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start a new investigation to begin analyzing threats, vulnerabilities, and security patterns.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {/* Render messages - observability is now embedded in each message */}
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    onEdit={editMessage}
                    onAddAsFinding={onAddAsFinding}
                  />
                ))}
                {isAgentTyping && currentStreamingMessage && (
                  <StreamingMessage
                    content={currentStreamingMessage}
                    investigationId={currentSession?.id || ''}
                  />
                )}
                {(isAgentTyping || isLoading) && agentActivity && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    mt: 1,
                    px: 2,
                    py: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {agentActivity}
                      {elapsedTime && ` (${elapsedTime})`}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </Container>
        ) : (
          <ChatEmptyState
            onNewInvestigation={onNewInvestigation}
            onOpenInvestigations={onOpenInvestigations}
          />
        )}
      </Box>

      {/* Chat Input - Only show when investigation is selected */}
      {currentSession && (
        <ChatInput
          onSendMessage={sendMessage}
          procedure={procedure}
          onCancel={cancelStream}
          disabled={isDisabled}
          isStreaming={isAgentTyping || isLoading}
          queuedCount={messageQueue.length}
          placeholder={
            currentSession?.state === 'archived'
              ? "This investigation is archived. Unarchive to continue..."
              : currentSession?.state === 'completed'
              ? "This investigation is completed. Reopen to continue..."
              : isAgentTyping || isLoading
              ? "Message will be queued..."
              : "Type your message..."
          }
        />
      )}
    </>
  );
});

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface;
